type ScriptStatus = "loading" | "loaded" | "error";

interface ScriptCache {
  [url: string]: {
    status: ScriptStatus;
    promise: Promise<void>;
  };
}

const scriptCache: ScriptCache = {};

export const loadScript = (
  src: string,
  options: {
    async?: boolean;
    defer?: boolean;
    id?: string;
  } = {}
): Promise<void> => {
  // If it is already loaded, return
  if (scriptCache[src]?.status === "loaded") {
    return Promise.resolve();
  }

  // If it is already loading, return the existing promise.
  if (scriptCache[src]?.status === "loading") {
    return scriptCache[src].promise;
  }

  // check if script is already loaded
  const existingScript = options.id
    ? document.getElementById(options.id)
    : document.querySelector(`script[src="${src}"]`);

  if (existingScript) {
    scriptCache[src] = {
      status: "loaded",
      promise: Promise.resolve(),
    };
    return Promise.resolve();
  }

  // Create a new promise
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = options.async ?? true;
    if (options.defer) script.defer = true;
    if (options.id) script.id = options.id;

    script.onload = () => {
      if (scriptCache[src]) {
        scriptCache[src].status = "loaded";
      }
      resolve();
    };

    script.onerror = () => {
      if (scriptCache[src]) {
        scriptCache[src].status = "error";
      }
      reject(new Error(`Failed to load script: ${src}`));
    };

    document.head.appendChild(script);
  });

  scriptCache[src] = {
    status: "loading",
    promise,
  };

  return promise;
};

export const isScriptLoaded = (src: string): boolean => {
  return scriptCache[src]?.status === "loaded";
};

export const clearScriptCache = () => {
  Object.keys(scriptCache).forEach((key) => delete scriptCache[key]);
};
